/**
 * The course-focus picker (UX_EXPERIENCE 4.3a, Issue 574): a button showing the focused course's symbol and short name,
 * and a popover listing the selected courses. Choosing one calls `setFocus`, which changes only what is shown. The name
 * always sits beside the symbol and the colour: neither is ever the only signal.
 */

import { Link } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import { useLocale, useMessages } from '../i18n/locale.js';
import { defineMessages } from '../i18n/messages.js';
import type { UiLocale } from '../i18n/locale.js';
import { isolate, useCourseFocus, type FocusCourse } from './course-focus.js';
import { CourseSymbol } from './course-symbol.js';
import { courseShortName, courseTitle } from './course-names.js';
import { withMorph } from './page-morph.js';

const messages = defineMessages({
  en: { course: 'Course', focusHeading: 'Course in focus', close: 'Close', manage: 'Courses and colours', noDue: 'No upcoming due dates' },
  he: { course: 'קורס', focusHeading: 'קורס במיקוד', close: 'סגירה', manage: 'קורסים וצבעים', noDue: 'אין מועדי הגשה קרובים' },
});

function formatDue(dueAt: string, locale: UiLocale): string {
  const at = new Date(dueAt);
  if (Number.isNaN(at.getTime())) return dueAt;
  return new Intl.DateTimeFormat(locale === 'he' ? 'he' : undefined, { weekday: 'short', day: 'numeric', month: 'short' }).format(at);
}

function Tile({ course }: { readonly course: FocusCourse }): React.JSX.Element {
  return (
    <span className="focus-tile" data-course={course.colour} aria-hidden="true">
      <CourseSymbol symbol={course.symbol} />
    </span>
  );
}

export function CourseFocusPicker(): React.JSX.Element | null {
  const t = useMessages(messages);
  const { locale } = useLocale();
  const { selectedCourses, focused, setFocus } = useCourseFocus();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    const selectedIndex = Math.max(0, selectedCourses.findIndex((c) => c.moodleCourseId === focused?.moodleCourseId));
    optionRefs.current[selectedIndex]?.focus();
    function onPointerDown(event: PointerEvent): void {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
    // Only on opening: later focus changes are the learner's own arrow keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (focused === null) return null;

  function close(): void {
    setOpen(false);
    buttonRef.current?.focus();
  }

  function choose(id: string): void {
    withMorph(() => setFocus(id));
    close();
  }

  function onListKeyDown(event: React.KeyboardEvent): void {
    const index = optionRefs.current.findIndex((el) => el === document.activeElement);
    const last = selectedCourses.length - 1;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      optionRefs.current[index >= last ? 0 : index + 1]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      optionRefs.current[index <= 0 ? last : index - 1]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      optionRefs.current[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      optionRefs.current[last]?.focus();
    }
  }

  const nameOf = (c: FocusCourse): string => courseTitle(c, locale);

  return (
    <li className="focus-picker" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="focus-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="course-focus-list"
        aria-label={`${t.course}: ${courseShortName(focused, locale)}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <Tile course={focused} />
        <bdi className="focus-name" dir="auto">
          {courseShortName(focused, locale)}
        </bdi>
        <svg className="focus-chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <>
          <button type="button" className="focus-scrim" tabIndex={-1} aria-label={t.close} onClick={() => setOpen(false)} />
          <div className="focus-popover" onKeyDown={onListKeyDown}>
          <div className="focus-head">
            <p className="focus-heading">{t.focusHeading}</p>
            <button type="button" className="focus-close" aria-label={t.close} onClick={close}>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <ul id="course-focus-list" role="listbox" aria-label={t.course} className="focus-options">
            {selectedCourses.map((c, i) => {
              const selected = c.moodleCourseId === focused.moodleCourseId;
              return (
                <li
                  key={c.moodleCourseId}
                  ref={(el) => {
                    optionRefs.current[i] = el;
                  }}
                  role="option"
                  aria-selected={selected}
                  tabIndex={-1}
                  className="focus-option"
                  data-course={c.colour}
                  onClick={() => choose(c.moodleCourseId)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      choose(c.moodleCourseId);
                    }
                  }}
                >
                  <Tile course={c} />
                  <span className="focus-option-text">
                    <bdi className="focus-option-name" dir="auto">
                      {courseShortName(c, locale)}
                    </bdi>
                    <span className="focus-option-title" title={`${c.code} · ${nameOf(c)}`}>
                      {c.code} &middot; {isolate(nameOf(c))}
                    </span>
                    {c.nextDue === null ? null : (
                      <span className="focus-due">
                        <bdi dir="auto">{c.nextDue.exerciseTitle}</bdi> &middot; {formatDue(c.nextDue.dueAt, locale)}
                      </span>
                    )}
                  </span>
                  {selected ? (
                    <svg className="focus-tick" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M5 12l5 5 9-10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <Link to="/settings/courses" className="focus-manage" onClick={() => setOpen(false)}>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <circle cx="8.5" cy="10" r="1.5" />
                <circle cx="12" cy="7.5" r="1.5" />
                <circle cx="15.5" cy="10" r="1.5" />
              </g>
            </svg>
            {t.manage}
          </Link>
          </div>
        </>
      ) : null}
    </li>
  );
}
